import {
  AutomationType,
  useUpdateCandidateBatchGroupComponentsMutation
} from 'generated-graphql/hooks/apollo-hooks';
import { AnalysisAutomationItem } from 'modules/analyse/types/AutomationTasks';
import { AutomationTaskActions } from 'modules/analyse/utils/formatAutomationTasksForSubmission';
import { useDrawer } from 'services/drawer/hooks';
import { ToastType, useToast } from 'services/toast';
import { getUpdateCandidateBatchGroupComponentsInput } from 'modules/analyse/utils/getUpdateCandidateBatchGroupComponentsInput';
import { FailedMessageWithHref } from 'common/ui';

type UseAnalyseUpdateCandidateBatchGroupProps = {
  automationType: AutomationType;
  initialAutomationTasks: AnalysisAutomationItem[];
  candidateBatchGroupName: string | undefined | null;
};

type SubmitFormProps = {
  candidateBatchGroupId: string;
  automationTasksActions: AutomationTaskActions;
  overwriteLockedCandidates: boolean;
  saveOnly: boolean;
};

type UseAnalyseUpdateCandidateBatchGroupReturn = {
  submitForm: (props: SubmitFormProps) => Promise<void>;
};

export const useAnalyseUpdateCandidateBatchGroup = ({
  automationType,
  initialAutomationTasks,
  candidateBatchGroupName
}: UseAnalyseUpdateCandidateBatchGroupProps): UseAnalyseUpdateCandidateBatchGroupReturn => {
  const { showToast } = useToast();
  const { forceCloseDrawer } = useDrawer();

  const [updateCandidateBatchGroupComponentsMutation] =
    useUpdateCandidateBatchGroupComponentsMutation();

  const submitForm = async ({
    candidateBatchGroupId,
    automationTasksActions,
    overwriteLockedCandidates,
    saveOnly
  }: SubmitFormProps): Promise<void> => {
    try {
      const input = getUpdateCandidateBatchGroupComponentsInput({
        initialAutomationTasks,
        candidateBatchGroupId,
        automationTasksActions,
        overwriteLockedCandidates,
        saveOnly
      });

      await updateCandidateBatchGroupComponentsMutation({
        variables: {
          input
        }
      });

      showToast({
        message: `${
          candidateBatchGroupName || 'Your Ruleset'
        } has been updated`,
        type: ToastType.Success
      });

      forceCloseDrawer();
    } catch (e) {
      const errorToastMessage =
        automationType === AutomationType.Rule
          ? 'updating the rules for the Ruleset'
          : 'applying the actions for the Ruleset';

      showToast({
        title: 'Something went wrong',
        message: (
          <FailedMessageWithHref
            failedMessage={`Try ${errorToastMessage} again.`}
            emailSubject={`Failed ${errorToastMessage}`}
          />
        ),
        type: ToastType.Error
      });
    }
  };

  return {
    submitForm
  };
};
